import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCampaignEmailHub } from './CampaignEmailHubContext';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { campaignEmailTemplateBannerImageUrl } from '../../../api/campaigns';
import CampaignEmailLivePreview from '../../../components/campaigns/CampaignEmailLivePreview';
import {
  campaignEmailTemplateId,
  campaignEmailTemplatesFromCampaign,
  templateUsesHubupVariable,
} from '../../../utils/campaignEmailTemplate';
import {
  hubupSendStateFromSource,
  resolveHubupPreviewNotification,
} from '../../../components/campaigns/HubupNotificationPickerFields';
import {
  hubupButtonDesignFromSource,
  productCardOptionsFromSource,
} from '../../../utils/emailButtonStyles';
import { deleteCampaignEmailTemplate } from '../../../api/campaigns';

export default function CampaignEmailLibraryRoute() {
  const { campaign, campaignId, canManageCampaigns } = useCampaignEmailHub();
  const { id } = useParams();
  const cid = campaignId || id;
  const base = `/campaigns/${encodeURIComponent(cid)}/email`;
  const [templates, setTemplates] = useState(() =>
    campaignEmailTemplatesFromCampaign(campaign),
  );
  const [deleting, setDeleting] = useState('');

  useEffect(() => {
    setTemplates(campaignEmailTemplatesFromCampaign(campaign));
  }, [campaign]);

  const handleDelete = async (templateId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Supprimer ce template ?')) return;
    setDeleting(templateId);
    try {
      await deleteCampaignEmailTemplate(cid, templateId);
      setTemplates((list) =>
        list.filter((t) => campaignEmailTemplateId(t) !== templateId),
      );
    } catch (err) {
      console.error(err);
      window.alert(err?.message || 'Suppression impossible.');
    } finally {
      setDeleting('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Templates email</h2>
          <p className="text-sm text-muted-foreground">
            Créez des modèles réutilisables pour l&apos;emailing et l&apos;annuaire.
          </p>
        </div>
        {canManageCampaigns ? (
          <Link to={`${base}/templates/new`}>
            <Button iconName="Plus" iconPosition="left">
              Nouveau template
            </Button>
          </Link>
        ) : null}
      </div>

      {templates.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 px-6 py-12 text-center">
          <Icon name="Mail" size={32} className="mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">Aucun template</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Commencez par un modèle avec objet et contenu.
          </p>
          {canManageCampaigns ? (
            <Link to={`${base}/templates/new`} className="mt-4 inline-block">
              <Button variant="outline" iconName="Plus" iconPosition="left">
                Créer un template
              </Button>
            </Link>
          ) : null}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => {
            const tid = campaignEmailTemplateId(template);
            const editPath = `${base}/templates/${encodeURIComponent(tid)}`;
            return (
              <article
                key={tid}
                className="group flex flex-col rounded-xl border border-border bg-card p-4 shadow-sm transition-shadow hover:shadow-md"
              >
                <Link to={editPath} className="mb-3 block min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="truncate font-semibold text-foreground group-hover:text-primary">
                        {template.name || 'Sans nom'}
                      </h3>
                      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                        {template.emailTemplateSubject || 'Sans objet'}
                      </p>
                    </div>
                    <Icon
                      name="ChevronRight"
                      size={18}
                      className="shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                    />
                  </div>
                  <div className="mt-3 overflow-hidden rounded-lg border border-border/70 bg-[#f2f2f2]">
                    <CampaignEmailLivePreview
                      variant="thumb"
                      sticky={false}
                      emailPreviewMode="send"
                      subject={template.emailTemplateSubject}
                      html={template.emailTemplateHtml}
                      bannerUrl={campaignEmailTemplateBannerImageUrl(campaign, template)}
                      productCardOptions={productCardOptionsFromSource(template)}
                      hubupNotification={
                        templateUsesHubupVariable(template.emailTemplateHtml)
                          ? resolveHubupPreviewNotification(
                              campaign,
                              hubupSendStateFromSource(template),
                              template,
                            )
                          : null
                      }
                      hubupButtonDesign={hubupButtonDesignFromSource(campaign)}
                    />
                  </div>
                </Link>
                <div className="mt-auto flex flex-wrap gap-2 pt-2 border-t border-border/60">
                  <Link
                    to={`${base}/send/${encodeURIComponent(tid)}`}
                    className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/15"
                  >
                    <Icon name="Send" size={14} />
                    Envoyer
                  </Link>
                  {canManageCampaigns ? (
                    <>
                      <Link
                        to={editPath}
                        className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-muted"
                      >
                        <Icon name="Pencil" size={14} />
                        Modifier
                      </Link>
                      <button
                        type="button"
                        onClick={(e) => handleDelete(tid, e)}
                        disabled={deleting === tid}
                        className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Icon name="Trash2" size={14} />
                        Supprimer
                      </button>
                    </>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
